import { useState, useEffect } from 'react'
import {
  BuildingStorefrontIcon,
  UsersIcon,
  CreditCardIcon,
  BellIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  DocumentTextIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline'
import { useAuthStore } from '../../stores/authStore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { settingService } from '../../services/settingService'
import { usePwaInstallStore, isStandalone } from '../../stores/pwaInstallStore'
import { InstallAppModal } from '../../components/pwa/InstallAppModal'
import toast from 'react-hot-toast'

const TABS = [
  { id: 'general', name: 'General', icon: BuildingStorefrontIcon },
  { id: 'documents', name: "Document TnC's", icon: DocumentTextIcon },
  { id: 'users', name: 'Users & Roles', icon: UsersIcon },
  { id: 'payment', name: 'Payment Methods', icon: CreditCardIcon },
  { id: 'notifications', name: 'Notifications', icon: BellIcon },
  { id: 'security', name: 'Security', icon: ShieldCheckIcon },
  { id: 'app', name: 'Install App', icon: DevicePhoneMobileIcon },
]

export const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('general')
  const { user } = useAuthStore()

  const isAdmin = (user?.role as any)?.role_name?.toLowerCase().includes('admin') || 
                  (user?.role as any)?.role_name?.toLowerCase().includes('super')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your business settings and preferences
        </p>
      </div>

      {/* Settings Layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-xl shadow border border-gray-100 p-2">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-primary-600' : 'text-gray-400'}`} />
                <span className="font-medium">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            {activeTab === 'general' && <GeneralSettings />}
            {activeTab === 'documents' && <DocumentSettings />}
            {activeTab === 'users' && isAdmin && <UsersSettings />}
            {activeTab === 'payment' && <PaymentSettings />}
            {activeTab === 'notifications' && <NotificationSettings />}
            {activeTab === 'security' && <SecuritySettings />}
            {activeTab === 'app' && <InstallAppSettings />}
            {!isAdmin && activeTab === 'users' && (
              <div className="text-center py-12">
                <ShieldCheckIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">Admin Access Required</h3>
                <p className="text-gray-500">Only administrators can manage users and roles</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Document Default T&C Settings
const DocumentSettings = () => {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['settings-documents'],
    queryFn: () => settingService.getAllSettings(),
  })

  const [form, setForm] = useState({
    default_quotation_terms: '',
    default_sale_terms: '',
    default_invoice_terms: '',
  })

  useEffect(() => {
    if (data?.data?.data) {
      const rows: any[] = data.data.data
      const get = (key: string) => rows.find((r: any) => r.setting_key === key)?.setting_value || ''
      setForm({
        default_quotation_terms: get('default_quotation_terms'),
        default_sale_terms: get('default_sale_terms'),
        default_invoice_terms: get('default_invoice_terms'),
      })
    }
  }, [data])

  const mutation = useMutation({
    mutationFn: async (values: typeof form) => {
      await Promise.all(
        Object.entries(values).map(([key, value]) =>
          settingService.updateSetting(key, { value, description: `Default T&C for ${key.replace('default_', '').replace('_terms', '')}` })
        )
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings-documents'] })
      toast.success('Document defaults saved')
    },
    onError: () => toast.error('Failed to save document defaults'),
  })

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>

  return (
    <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form) }} className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Default Terms & Conditions</h2>
        <p className="text-sm text-gray-500 mt-1">These auto-fill when creating new documents. You can still edit them per document.</p>
      </div>

      {([
        { key: 'default_quotation_terms', label: 'Quotation T&C' },
        { key: 'default_sale_terms', label: 'Sale T&C' },
        { key: 'default_invoice_terms', label: 'Invoice T&C' },
      ] as const).map(({ key, label }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
          <textarea
            value={form[key]}
            onChange={(e) => setForm(p => ({ ...p, [key]: e.target.value }))}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            placeholder={`Default terms and conditions for ${label.toLowerCase()}...`}
          />
        </div>
      ))}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Save Defaults'}
        </button>
      </div>
    </form>
  )
}

// General Settings Component
const GeneralSettings = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => settingService.getCompanySettings(),
  })

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    taxNumber: '',
    receiptFooter: '',
  })

  useEffect(() => {
    if (data?.data?.data) {
      const d = data.data.data
      setForm({
        name: d.name || '',
        email: d.email || '',
        phone: d.phone || '',
        address: d.address || '',
        taxNumber: d.taxNumber || '',
        receiptFooter: d.receiptFooter || '',
      })
    }
  }, [data])

  const mutation = useMutation({
    mutationFn: (payload: any) => settingService.updateCompanySettings(payload),
    onSuccess: () => {
      toast.success('Company settings saved successfully')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to save settings')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(form)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Business Information</h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">KRA PIN / Tax Number</label>
          <input
            type="text"
            value={form.taxNumber}
            onChange={(e) => setForm(p => ({ ...p, taxNumber: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            value={form.address}
            onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Footer Text</label>
          <textarea
            value={form.receiptFooter}
            onChange={(e) => setForm(p => ({ ...p, receiptFooter: e.target.value }))}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Thank you for your business!"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

// Users Settings Component
const UsersSettings = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Users & Roles</h2>
        <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          Add User
        </button>
      </div>

      <div className="border rounded-lg divide-y">
        {[
          { name: 'Super Admin', users: 1, permissions: 'Full Access' },
          { name: 'Administrator', users: 1, permissions: 'Most Features' },
          { name: 'Sales Manager', users: 1, permissions: 'Sales + Reports' },
          { name: 'Sales Person', users: 1, permissions: 'POS + Sales' },
          { name: 'Inventory Manager', users: 1, permissions: 'Inventory Only' },
          { name: 'Accountant', users: 1, permissions: 'Financial Only' },
          { name: 'Viewer', users: 1, permissions: 'Read Only' },
        ].map((role) => (
          <div key={role.name} className="p-4 flex items-center justify-between hover:bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">{role.name}</p>
              <p className="text-sm text-gray-500">{role.permissions}</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">{role.users} users</span>
              <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Payment Settings Component
const PaymentSettings = () => {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Payment Methods</h2>

      <div className="space-y-4">
        {[
          { name: 'Cash', enabled: true, icon: BanknotesIcon },
          { name: 'M-Pesa', enabled: true, icon: CreditCardIcon },
          { name: 'Credit Card', enabled: true, icon: CreditCardIcon },
          { name: 'Bank Transfer', enabled: true, icon: BanknotesIcon },
          { name: 'Cheque', enabled: false, icon: BanknotesIcon },
          { name: 'Credit (On Account)', enabled: true, icon: CreditCardIcon },
        ].map((method) => (
          <div key={method.name} className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <method.icon className="w-5 h-5 text-gray-600" />
              </div>
              <span className="font-medium text-gray-900">{method.name}</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked={method.enabled} className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        ))}
      </div>
    </div>
  )
}

// Notification Settings Component
const NotificationSettings = () => {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>

      <div className="space-y-4">
        {[
          { name: 'Low Stock Alerts', description: 'Get notified when products fall below reorder level', enabled: true },
          { name: 'Expiry Warnings', description: 'Alert when serial numbers approach warranty expiry', enabled: true },
          { name: 'Payment Notifications', description: 'Receive notifications for payment received', enabled: true },
          { name: 'Consignment Reminders', description: 'Reminders for consignment stock recall dates', enabled: true },
          { name: 'Daily Summary', description: 'Email summary of daily sales and activities', enabled: false },
        ].map((setting) => (
          <div key={setting.name} className="flex items-start justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium text-gray-900">{setting.name}</p>
              <p className="text-sm text-gray-500">{setting.description}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked={setting.enabled} className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        ))}
      </div>
    </div>
  )
}

// Install App Settings Component
const InstallAppSettings = () => {
  const installed = usePwaInstallStore((s) => s.installed) || isStandalone()
  const [showModal, setShowModal] = useState(false)

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Install App</h2>

      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <p className="font-medium text-gray-900">SnapShot on your device</p>
          <p className="text-sm text-gray-500">
            {installed
              ? "You're using the installed app."
              : 'Add SnapShot to your Home Screen for quick, full-screen access.'}
          </p>
        </div>
        {!installed && (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Install App
          </button>
        )}
      </div>

      {showModal && <InstallAppModal onClose={() => setShowModal(false)} />}
    </div>
  )
}

// Security Settings Component
const SecuritySettings = () => {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Security Settings</h2>

      <div className="space-y-6">
        <div className="p-4 border rounded-lg">
          <h3 className="font-medium text-gray-900 mb-2">Change Password</h3>
          <div className="space-y-3">
            <input
              type="password"
              placeholder="Current Password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="password"
              placeholder="New Password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
              Update Password
            </button>
          </div>
        </div>

        <div className="p-4 border rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Two-Factor Authentication</h3>
              <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
            </div>
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Enable 2FA
            </button>
          </div>
        </div>

        <div className="p-4 border rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Session Management</h3>
              <p className="text-sm text-gray-500">Manage your active sessions and devices</p>
            </div>
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              View Sessions
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
